import React from "react";
import {
  Box,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ScorePicker from "./ScorePicker";
import {
  CRITERIA,
  STATUS_STYLE,
  formatEvaluator,
  canEditCriterion,
  canSaveEvaluations,
  showsEvaluatorStamp,
} from "../constants";

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.inactive;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.875,
        py: 0.25,
        borderRadius: "6px",
        fontSize: "0.65rem",
        fontWeight: 600,
        color: s.color,
        bgcolor: s.bg,
        ml: 1,
      }}
    >
      {s.label}
    </Box>
  );
}

function ScoreCell({ criterion, row, role, techId, onChange }) {
  const value = row[criterion.field] ?? 0;
  const editable = canEditCriterion(role, criterion.id);
  const showStamp = showsEvaluatorStamp(criterion.id);
  const evaluatorLabel = showStamp ? formatEvaluator(row.evaluators?.[criterion.evaluatorKey]) : null;

  return (
    <Box>
      <ScorePicker
        value={value}
        onChange={(v) => onChange(techId, criterion.field, v)}
        compact
        accent={criterion.color}
        disabled={!editable}
      />
      {showStamp && (
        evaluatorLabel ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#6b7280", fontSize: "0.65rem", lineHeight: 1.3 }}>
            {evaluatorLabel}
          </Typography>
        ) : value > 0 ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#9ca3af", fontSize: "0.65rem" }}>
            Sin registro de evaluador
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#d1d5db", fontSize: "0.65rem" }}>
            Pendiente
          </Typography>
        )
      )}
    </Box>
  );
}

function overviewChipLabel(role) {
  if (role === "rh") return "RH edita Calidad, Puntualidad y Actitud";
  if (role === "admin") return "Admin edita Cumpl. Admin · ve quién calificó el resto";
  return "Vista general";
}

export default function AdminOverviewTable({
  technicians,
  rows,
  role,
  onChange,
  onSave,
  savingId,
}) {
  const canSave = canSaveEvaluations(role);
  const canEditNotes = canSave;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <VisibilityIcon sx={{ fontSize: 18, color: "#1975d1" }} />
        <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.9rem" }}>
          Vista general — todas las calificaciones
        </Typography>
        <Chip
          label={overviewChipLabel(role)}
          size="small"
          sx={{ fontSize: "0.65rem", height: 22, bgcolor: "#eef5fd", color: "#1975d1" }}
        />
      </Stack>

      <TableContainer sx={{ overflowX: "auto", border: "1px solid #e8eaef", borderRadius: "14px" }}>
        <Table sx={{ width: "100%", tableLayout: "fixed", minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx("16%")}>Técnico</TableCell>
              {CRITERIA.map((c) => (
                <TableCell key={c.id} sx={headCellSx("17%")}>
                  {c.shortLabel}
                </TableCell>
              ))}
              <TableCell sx={headCellSx("12%")}>Notas</TableCell>
              {canSave && <TableCell sx={headCellSx(48)} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {technicians.map((tech) => {
              const row = rows[tech._id] || {};
              const saving = savingId === tech._id;

              return (
                <TableRow
                  key={tech._id}
                  sx={{
                    opacity: tech.status === "inactive" ? 0.65 : 1,
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover td": { bgcolor: "#fafbff" },
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.85rem" }}>
                      {tech.name}
                      <StatusBadge status={tech.status} />
                    </Typography>
                    {tech.zone && (
                      <Typography variant="caption" sx={{ color: "#9ca3af", display: "block" }}>
                        {tech.zone}
                      </Typography>
                    )}
                  </TableCell>
                  {CRITERIA.map((c) => (
                    <TableCell key={c.id} sx={bodyCellSx}>
                      <ScoreCell
                        criterion={c}
                        row={row}
                        role={role}
                        techId={tech._id}
                        onChange={onChange}
                      />
                    </TableCell>
                  ))}
                  <TableCell sx={bodyCellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="—"
                      value={row.notes ?? ""}
                      onChange={(e) => onChange(tech._id, "notes", e.target.value)}
                      disabled={!canEditNotes}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          fontSize: "0.8rem",
                          "& fieldset": { borderColor: "#e8eaef" },
                        },
                      }}
                    />
                  </TableCell>
                  {canSave && (
                    <TableCell align="center" sx={bodyCellSx}>
                      <Tooltip title={role === "admin" ? "Guardar cumplimiento administrativo" : "Guardar evaluación"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onSave(tech._id)}
                            disabled={saving}
                            sx={{
                              bgcolor: role === "admin" ? "#8b5cf6" : "#1975d1",
                              color: "#fff",
                              width: 32,
                              height: 32,
                              "&:hover": { bgcolor: role === "admin" ? "#7c3aed" : "#1565c0" },
                              "&.Mui-disabled": {
                                bgcolor: role === "admin" ? "#ede9fe" : "#e3f2fd",
                                color: role === "admin" ? "#c4b5fd" : "#90caf9",
                              },
                            }}
                          >
                            {saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const headCellSx = (width) => ({
  fontWeight: 600,
  fontSize: "0.65rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#9ca3af",
  borderBottom: "1px solid #e8eaef",
  py: 1.5,
  width,
  bgcolor: "#fafbfc",
});

const bodyCellSx = {
  borderBottom: "1px solid #f3f4f6",
  py: 1.75,
  verticalAlign: "top",
};
